import type { JsonObject, ToolDef } from '@fabric-harness/sdk';
import type { DatabricksModelServingQueryClient } from './embeddings.js';

export interface DatabricksFeatureLookupToolOptions {
  /** Feature Serving endpoint name (serves features from an online table / feature spec). */
  endpoint: string;
  name?: string;
  description?: string;
}

/**
 * Low-latency feature lookup from a Databricks **Feature Serving** endpoint (backed by an Online
 * Table). The model passes primary-key records; the endpoint returns the served feature values.
 * Drives serving consumption and keeps features governed by Unity Catalog under the bundle principal.
 */
export function databricksFeatureLookupTool(client: DatabricksModelServingQueryClient, options: DatabricksFeatureLookupToolOptions): ToolDef<{ records: JsonObject[] }, unknown> {
  return {
    name: options.name ?? 'databricks_feature_lookup',
    description: options.description ?? 'Look up low-latency features for primary-key records from a Databricks Feature Serving endpoint.',
    inputSchema: {
      type: 'object',
      additionalProperties: false,
      properties: {
        records: {
          type: 'array',
          items: { type: 'object', additionalProperties: true },
          minItems: 1,
          description: 'Primary-key records, e.g. [{ "user_id": 123 }].',
        },
      },
      required: ['records'],
    },
    metadata: { effect: 'read', provider: 'databricks', service: 'feature-serving' },
    execute: (input) => retryReadOnly(() => client.query({
      name: options.endpoint,
      dataframeRecords: input.records as never,
    })),
  };
}

async function retryReadOnly<T>(call: () => Promise<T>, attempts = 3): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await call();
    } catch (error) {
      last = error;
      const status = (error as { httpStatusCode?: unknown }).httpStatusCode;
      if (status !== 429 && (typeof status !== 'number' || status < 500 || status >= 600)) throw error;
      if (attempt + 1 < attempts) await new Promise((resolve) => setTimeout(resolve, 2 ** attempt * 25));
    }
  }
  throw last;
}
